import { Injectable } from '@angular/core';
import { ColDef } from 'ag-grid-community';
import { ActionsCellRendererComponent } from '../ag-grid-table/cell-renderers/render-components/actions/actions.component';
import { TextCellRendererComponent } from '../ag-grid-table/cell-renderers/render-components/text/text.component';
import { BooleanEditorComponent } from '../ag-grid-table/cell-editors/boolean/boolean.component';
import { RefsCellRendererComponent } from '../ag-grid-table/cell-renderers/render-components/refs/refs.component';
import { AgGridToolsService } from '../ag-grid-table/services/ag-grid-tools.service';
import { CalendarEditorComponent } from '../ag-grid-table/cell-editors/calendar/calendar.component';

export interface TableConfig {
  entity: string;
  columnDefs: ColDef[];
  tableParams: any;
  buildParams: any;
  bulkUpdateColDefs: any[];
}

@Injectable({
  providedIn: 'root'
})
export class DynamicTableConfigService {
  private tableConfigs: { [key: string]: TableConfig };

  constructor(private agGridToolsService: AgGridToolsService) {
    this.initializeTableConfigs();
  }

  private initializeTableConfigs() {
    this.tableConfigs = {
      'link-destination': {
        entity: 'link-destination',
        columnDefs: [
          {
            headerName: 'Actions',
            field: 'actions',
            cellRenderer: ActionsCellRendererComponent,
            pinned: 'right',
            width: 120,
            resizable: true,
          },
          {
            headerName: 'ID',
            field: 'id',
            width: 125,
            headerCheckboxSelection: true,
            checkboxSelection: true,
            sortable: true,
            initialSort: 'desc',
            sortIndex: 0,
            initialSortIndex: 0,
            sort: 'desc',
            sortingOrder: ['asc','desc'],
            unSortIcon: false,
            filter: 'agNumberColumnFilter',
            filterParams: {
              filterOptions: [
                'equals',
                'notEqual',
                'lessThan',
                'lessThanOrEqual',
                'greaterThan',
                'greaterThanOrEqual',
                'inRange'
              ]
            },
            resizable: true,
            floatingFilter: true,
            suppressSizeToFit: true,
          },
          {
            headerName: 'Link Text',
            field: 'linkText',
            cellRendererParams: {
              entity: 'link-destination'
            },
            cellRenderer: TextCellRendererComponent,
            sortable: true,
            unSortIcon: true,
            resizable: true,
            filter: 'agTextColumnFilter',
            floatingFilter: true,
            autoHeight: true,
            cellEditor: 'agTextCellEditor',
            type: 'editableColumn',
          },
          {
            headerName: 'Link Destination',
            field: 'linkDestination',
            cellRendererParams: {
              entity: 'link-destination'
            },
            cellRenderer: TextCellRendererComponent,
            sortable: true,
            unSortIcon: true,
            resizable: true,
            filter: 'agTextColumnFilter',
            floatingFilter: true,
            autoHeight: true,
            cellEditor: 'agTextCellEditor',
            type: 'editableColumn',
          },
          {
            headerName: 'Enabled',
            field: 'enabled',
            cellRenderer: TextCellRendererComponent,
            cellRendererParams: {
              onLabel: 'Enabled',
              offLabel: 'Not Enabled',
              isBoolean: true
            },
            sortable: true,
            unSortIcon: true,
            resizable: true,
            filter: 'agSetColumnFilter',
            filterParams: {
              values: [true, false],
              valueFormatter: (params: any) => params.value ? 'Enabled' : 'Not Enabled'
            },
            floatingFilter: true,
            cellEditor: BooleanEditorComponent,
            type: 'editableColumn',
          },
        ],
        tableParams: {
          customActionsLabel: 'Custom Bulk Actions',
          customActionButtons: ['bulkLocalize'],
          localizeCollectionName: 'link-destination',
        },
        buildParams: {},
        bulkUpdateColDefs: []
      },
      // Add more table configurations here as needed
      'scene-types': {
        entity: 'scene-types',
        columnDefs: [
          {
            headerName: 'Actions',
            field: 'actions',
            cellRenderer: ActionsCellRendererComponent,
            pinned: 'right',
            width: 120,
            resizable: true,
          },
          {
            headerName: 'ID',
            field: 'id',
            width: 125,
            headerCheckboxSelection: true,
            checkboxSelection: true,
            sortable: true,
            initialSort: 'desc',
            sortIndex: 0,
            initialSortIndex: 0,
            sort: 'desc',
            sortingOrder: ['asc','desc'],
            unSortIcon: false,
            filter: 'agNumberColumnFilter',
            filterParams: {
              filterOptions: [
                'equals',
                'notEqual',
                'lessThan',
                'lessThanOrEqual',
                'greaterThan',
                'greaterThanOrEqual',
                'inRange'
              ]
            },
            resizable: true,
            floatingFilter: true,
            suppressSizeToFit: true,
          },
          {
            headerName: 'Name',
            field: 'name',
            cellRenderer: TextCellRendererComponent,
            sortable: true,
            unSortIcon: true,
            resizable: true,
            filter: 'agTextColumnFilter',
            floatingFilter: true,
            autoHeight: true,
            cellEditor: 'agTextCellEditor',
            type: 'editableColumn',
          },
          {
            headerName: 'Path Name',
            field: 'pathName',
            cellRenderer: TextCellRendererComponent,
            sortable: true,
            unSortIcon: true,
            resizable: true,
            filter: 'agTextColumnFilter',
            floatingFilter: true,
            autoHeight: true,
            cellEditor: 'agTextCellEditor',
            type: 'editableColumn',
          },
          {
            headerName: 'Enabled',
            field: 'enabled',
            cellRenderer: TextCellRendererComponent,
            cellRendererParams: {
              onLabel: 'Enabled',
              offLabel: 'Not Enabled',
              isBoolean: true
            },
            sortable: true,
            unSortIcon: true,
            resizable: true,
            filter: 'agSetColumnFilter',
            filterParams: {
              values: [true, false],
              valueFormatter: (params: any) => params.value ? 'Enabled' : 'Not Enabled'
            },
            floatingFilter: true,
            cellEditor: BooleanEditorComponent,
            type: 'editableColumn',
          },
          {
            headerName: 'Created By',
            field: 'createdBy',
            cellRenderer: TextCellRendererComponent,
            sortable: true,
            unSortIcon: true,
            resizable: true,
            filter: 'agTextColumnFilter',
            floatingFilter: true,
            autoHeight: true,
            cellEditor: 'agTextCellEditor',
            type: 'editableColumn',
          },
          {
            headerName: 'Created At',
            field: 'createdAt',
            cellRenderer: TextCellRendererComponent,
            sortable: true,
            unSortIcon: true,
            resizable: true,
            filter: 'agDateColumnFilter',
            floatingFilter: true,
            autoHeight: true,
          },
        ],
        tableParams: {},
        buildParams: {},
        bulkUpdateColDefs: []
      },
      'entity-type-settings': {
        entity: 'entity-type-settings',
        columnDefs: [
          {
            headerName: 'Actions',
            field: 'actions',
            cellRenderer: ActionsCellRendererComponent,
            pinned: 'right',
            width: 120,
            resizable: true,
          },
          {
            headerName: 'ID',
            field: 'id',
            width: 125,
            headerCheckboxSelection: true,
            checkboxSelection: true,
            sortable: true,
            initialSort: 'desc',
            sortIndex: 0,
            initialSortIndex: 0,
            sort: 'desc',
            sortingOrder: ['asc','desc'],
            unSortIcon: false,
            filter: 'agNumberColumnFilter',
            filterParams: {
              filterOptions: [
                'equals',
                'notEqual',
                'lessThan',
                'lessThanOrEqual',
                'greaterThan',
                'greaterThanOrEqual',
                'inRange'
              ]
            },
            resizable: true,
            floatingFilter: true,
            suppressSizeToFit: true,
          },
          {
            headerName: 'Name',
            field: 'name',
            cellRenderer: TextCellRendererComponent,
            sortable: true,
            unSortIcon: true,
            resizable: true,
            filter: 'agTextColumnFilter',
            floatingFilter: true,
            autoHeight: true,
            cellEditor: 'agTextCellEditor',
            type: 'editableColumn',
          },
          {
            headerName: 'Show on Asset Bundle Table',
            field: 'showOnAssetBundleOnly',
            cellRenderer: TextCellRendererComponent,
            cellRendererParams: {
              onLabel: 'Enabled',
              offLabel: 'Not Enabled',
              isBoolean: true
            },
            sortable: true,
            unSortIcon: true,
            resizable: true,
            filter: 'agSetColumnFilter',
            filterParams: {
              values: [true, false],
              valueFormatter: (params: any) => params.value ? 'Enabled' : 'Not Enabled'
            },
            floatingFilter: true,
            cellEditor: BooleanEditorComponent,
            type: 'editableColumn',
          },
          {
            headerName: 'Enabled',
            field: 'enabled',
            cellRenderer: TextCellRendererComponent,
            cellRendererParams: {
              onLabel: 'Enabled',
              offLabel: 'Not Enabled',
              isBoolean: true
            },
            sortable: true,
            unSortIcon: true,
            resizable: true,
            filter: 'agSetColumnFilter',
            filterParams: {
              values: [true, false],
              valueFormatter: (params: any) => params.value ? 'Enabled' : 'Not Enabled'
            },
            floatingFilter: true,
            cellEditor: BooleanEditorComponent,
            type: 'editableColumn',
          },
          {
            headerName: 'Created By',
            field: 'createdBy',
            cellRenderer: RefsCellRendererComponent,
            sortable: true,
            unSortIcon: true,
            resizable: true,
            filter: 'agSetColumnFilter',
            filterParams: {
              values: [],
              valueFormatter: (params: any) => {
                return `${params.value.name}`;
              },
              keyCreator: (params: any) => {
                return params.value._id;
              },
              comparator: (a: any, b: any) => this.agGridToolsService.nameASC(a, b),
              isSingleRefFilter: true,
              populate: true,
              select: '_id id name'
            },
            floatingFilter: true,
            autoHeight: true,
          },
          {
            headerName: 'Created At',
            field: 'createdAt',
            sortable: true,
            unSortIcon: true,
            filter: 'agDateColumnFilter',
            filterParams: {
              buttons: ['clear'],
            },
            floatingFilter: true,
            suppressSizeToFit: true,
            cellRenderer: TextCellRendererComponent,
            cellRendererParams: {
              isDate: true,
            },
            valueGetter: this.agGridToolsService.dateValueGetter('createdAt'),
            resizable: true,
            width: 300,
            autoHeight: true,
            cellStyle: {
              padding: '10px',
              whiteSpace: 'normal',
              lineHeight: '1.5',
            },
            cellClass: 'custom-cell-style',
          },
        ],
        tableParams: {},
        buildParams: {},
        bulkUpdateColDefs: []
      },
      'ImagerySettings': {
        entity: 'ImagerySettings',
        columnDefs: [
          {
            headerName: 'Actions',
            field: 'actions',
            cellRenderer: ActionsCellRendererComponent,
            pinned: 'right',
            width: 120,
            resizable: true,
          },
          {
            headerName: 'ID',
            field: 'id',
            width: 125,
            headerCheckboxSelection: true,
            checkboxSelection: true,
            sortable: true,
            initialSort: 'desc',
            sortIndex: 0,
            initialSortIndex: 0,
            sort: 'desc',
            sortingOrder: ['asc','desc'],
            unSortIcon: false,
            filter: 'agNumberColumnFilter',
            filterParams: {
              filterOptions: [
                'equals',
                'notEqual',
                'lessThan',
                'lessThanOrEqual',
                'greaterThan',
                'greaterThanOrEqual',
                'inRange'
              ]
            },
            resizable: true,
            floatingFilter: true,
            suppressSizeToFit: true,
          },
          {
            headerName: 'Name',
            field: 'name',
            cellRenderer: TextCellRendererComponent,
            sortable: true,
            unSortIcon: true,
            resizable: true,
            filter: 'agTextColumnFilter',
            floatingFilter: true,
            autoHeight: true,
            cellEditor: 'agTextCellEditor',
            type: 'editableColumn',
          },
          {
            headerName: 'Path',
            field: 'path',
            cellRenderer: TextCellRendererComponent,
            sortable: true,
            unSortIcon: true,
            resizable: true,
            filter: 'agTextColumnFilter',
            floatingFilter: true,
            autoHeight: true,
            cellEditor: 'agTextCellEditor',
            type: 'editableColumn',
          },
          {
            headerName: 'Entity Type',
            field: 'entityType',
            cellRenderer: TextCellRendererComponent,
            sortable: true,
            unSortIcon: true,
            resizable: true,
            filter: 'agTextColumnFilter',
            floatingFilter: true,
            autoHeight: true,
          },
          {
            headerName: 'Enabled',
            field: 'enabled',
            cellRenderer: TextCellRendererComponent,
            cellRendererParams: {
              onLabel: 'Enabled',
              offLabel: 'Not Enabled',
              isBoolean: true
            },
            sortable: true,
            unSortIcon: true,
            resizable: true,
            filter: 'agSetColumnFilter',
            filterParams: {
              values: [true, false],
              valueFormatter: (params: any) => params.value ? 'Enabled' : 'Not Enabled'
            },
            floatingFilter: true,
            cellEditor: BooleanEditorComponent,
            type: 'editableColumn',
          },
          {
            headerName: 'Show on Image Bundle Table',
            field: 'showOnImageTypeList',
            cellRenderer: TextCellRendererComponent,
            cellRendererParams: {
              onLabel: 'true',
              offLabel: 'false',
              isBoolean: true
            },
            sortable: true,
            unSortIcon: true,
            resizable: true,
            filter: 'agSetColumnFilter',
            filterParams: {
              values: [true, false],
              valueFormatter: (params: any) => params.value ? 'true' : 'false'
            },
            floatingFilter: true,
            cellEditor: BooleanEditorComponent,
            type: 'editableColumn',
          },
          {
              headerName: 'Show on Asset Bundle Table',
            field: 'showOnAssetBundleOnly',
            cellRenderer: TextCellRendererComponent,
            cellRendererParams: {
              onLabel: 'true',
              offLabel: 'false',
              isBoolean: true
            },
            sortable: true,
            unSortIcon: true,
            resizable: true,
            filter: 'agSetColumnFilter',
            filterParams: {
              values: [true, false],
              valueFormatter: (params: any) => params.value ? 'true' : 'false'
            },
            floatingFilter: true,
            cellEditor: BooleanEditorComponent,
            type: 'editableColumn',
          },
          {
            headerName: 'Created By',
            field: 'createdBy',
            cellRenderer: RefsCellRendererComponent,
            sortable: true,
            unSortIcon: true,
            resizable: true,
            filter: 'agSetColumnFilter',
            filterParams: {
              values: [],
              valueFormatter: (params: any) => {
                return `${params.value.name}`;
              },
              keyCreator: (params: any) => {
                return params.value._id;
              },
              comparator: (a: any, b: any) => this.agGridToolsService.nameASC(a, b),
              isSingleRefFilter: true,
              populate: true,
              select: '_id id name'
            },
            floatingFilter: true,
            autoHeight: true,
          },
          {
            headerName: 'Created At',
            field: 'createdAt',
            sortable: true,
            unSortIcon: true,
            filter: 'agDateColumnFilter',
            filterParams: {
              buttons: ['clear'],
            },
            floatingFilter: true,
            suppressSizeToFit: true,
            cellRenderer: TextCellRendererComponent,
            cellRendererParams: {
              isDate: true,
            },
            valueGetter: this.agGridToolsService.dateValueGetter('createdAt'),
            resizable: true,
            type: 'editableColumn',
            cellEditor: CalendarEditorComponent,
            width: 300,
            autoHeight: true,
            cellStyle: {
              padding: '10px',
              whiteSpace: 'normal',
              lineHeight: '1.5',
            },
            cellClass: 'custom-cell-style',
          },
        ],
        tableParams: {},
        buildParams: {},
        bulkUpdateColDefs: []
      },
      'tags': {
        entity: 'tags',
        columnDefs: [
          {
            headerName: 'Actions',
            field: 'actions',
            cellRenderer: ActionsCellRendererComponent,
            pinned: 'right',
            width: 120,
            resizable: true,
          },
          {
            headerName: 'ID',
            field: 'id',
            width: 125,
            headerCheckboxSelection: true,
            checkboxSelection: true,
            sortable: true,
            initialSort: 'desc',
            sortIndex: 0,
            initialSortIndex: 0,
            sort: 'desc',
            sortingOrder: ['asc','desc'],
            unSortIcon: false,
            filter: 'agNumberColumnFilter',
            filterParams: {
              filterOptions: [
                'equals',
                'notEqual',
                'lessThan',
                'lessThanOrEqual',
                'greaterThan',
                'greaterThanOrEqual',
                'inRange'
              ]
            },
            resizable: true,
            floatingFilter: true,
            suppressSizeToFit: true,
          },
          {
            headerName: 'Tag Name',
            field: 'name',
            cellRenderer: TextCellRendererComponent,
            sortable: true,
            unSortIcon: true,
            resizable: true,
            filter: 'agTextColumnFilter',
            floatingFilter: true,
            autoHeight: true,
            cellEditor: 'agTextCellEditor',
            type: 'editableColumn',
          },
          {
            headerName: 'Created By',
            field: 'createdBy',
            cellRenderer: RefsCellRendererComponent,
            sortable: true,
            unSortIcon: true,
            resizable: true,
            filter: 'agSetColumnFilter',
            filterParams: {
              values: [],
              valueFormatter: (params: any) => {
                return `${params.value.name}`;
              },
              keyCreator: (params: any) => {
                return params.value._id;
              },
              comparator: (a: any, b: any) => this.agGridToolsService.nameASC(a, b),
              isSingleRefFilter: true,
              populate: true,
              select: '_id id name'
            },
            floatingFilter: true,
            autoHeight: true,
          },
          {
            headerName: 'Created At',
            field: 'createdAt',
            sortable: true,
            unSortIcon: true,
            filter: 'agDateColumnFilter',
            filterParams: {
              buttons: ['clear'],
            },
            floatingFilter: true,
            suppressSizeToFit: true,
            cellRenderer: TextCellRendererComponent,
            cellRendererParams: {
              isDate: true,
            },
            valueGetter: this.agGridToolsService.dateValueGetter('createdAt'),
            resizable: true,
            type: 'editableColumn',
            cellEditor: CalendarEditorComponent,
            width: 300,
            autoHeight: true,
            cellStyle: {
              padding: '10px',
              whiteSpace: 'normal',
              lineHeight: '1.5',
            },
            cellClass: 'custom-cell-style',
          },
        ],
        tableParams: {},
        buildParams: {},
        bulkUpdateColDefs: []
      },
      'content-hold': {
        entity: 'content-hold',
        columnDefs: [
          {
            headerName: 'Actions',
            field: 'actions',
            cellRenderer: ActionsCellRendererComponent,
            pinned: 'right',
            width: 120,
            resizable: true,
          },
          {
            headerName: 'ID',
            field: 'id',
            width: 125,
            headerCheckboxSelection: true,
            checkboxSelection: true,
            sortable: true,
            initialSort: 'desc',
            sortIndex: 0,
            initialSortIndex: 0,
            sort: 'desc',
            sortingOrder: ['asc','desc'],
            unSortIcon: false,
            filter: 'agNumberColumnFilter',
            filterParams: {
              filterOptions: [
                'equals',
                'notEqual',
                'lessThan',
                'lessThanOrEqual',
                'greaterThan',
                'greaterThanOrEqual',
                'inRange'
              ]
            },
            resizable: true,
            floatingFilter: true,
            suppressSizeToFit: true,
          },
          {
            headerName: 'Name',
            field: 'name',
            cellRenderer: TextCellRendererComponent,
            sortable: true,
            unSortIcon: true,
            resizable: true,
            filter: 'agTextColumnFilter',
            floatingFilter: true,
            autoHeight: true,
            cellEditor: 'agTextCellEditor',
            type: 'editableColumn',
          },
          {
            headerName: 'Enabled',
            field: 'enabled',
            cellRenderer: TextCellRendererComponent,
            cellRendererParams: {
              onLabel: 'Enabled',
              offLabel: 'Not Enabled',
              isBoolean: true
            },
            sortable: true,
            unSortIcon: true,
            resizable: true,
            filter: 'agSetColumnFilter',
            filterParams: {
              values: [true, false],
              valueFormatter: (params: any) => params.value ? 'Enabled' : 'Not Enabled'
            },
            floatingFilter: true,
            cellEditor: BooleanEditorComponent,
            type: 'editableColumn',
          },
          {
            headerName: 'Created By',
            field: 'createdBy',
            cellRenderer: RefsCellRendererComponent,
            sortable: true,
            unSortIcon: true,
            resizable: true,
            filter: 'agSetColumnFilter',
            filterParams: {
              values: [],
              valueFormatter: (params: any) => {
                return `${params.value.name}`;
              },
              keyCreator: (params: any) => {
                return params.value._id;
              },
              comparator: (a: any, b: any) => this.agGridToolsService.nameASC(a, b),
              isSingleRefFilter: true,
              populate: true,
              select: '_id id name'
            },
            floatingFilter: true,
            autoHeight: true,
          },
          {
            headerName: 'Created At',
            field: 'createdAt',
            sortable: true,
            unSortIcon: true,
            filter: 'agDateColumnFilter',
            filterParams: {
              buttons: ['clear'],
            },
            floatingFilter: true,
            suppressSizeToFit: true,
            cellRenderer: TextCellRendererComponent,
            cellRendererParams: {
              isDate: true,
            },
            valueGetter: this.agGridToolsService.dateValueGetter('createdAt'),
            resizable: true,
            type: 'editableColumn',
            cellEditor: CalendarEditorComponent,
            width: 300,
            autoHeight: true,
            cellStyle: {
              padding: '10px',
              whiteSpace: 'normal',
              lineHeight: '1.5',
            },
            cellClass: 'custom-cell-style',
          },    
        ],
        tableParams: {},
        buildParams: {},
        bulkUpdateColDefs: []
      },
      'challenge-content-hold': {
        entity: 'challenge-content-hold',
        columnDefs: [
          {
            headerName: 'Actions',
            field: 'actions',
            cellRenderer: ActionsCellRendererComponent,
            pinned: 'right',
            width: 120,
            resizable: true,
          },
          {
            headerName: 'ID',
            field: 'id',
            width: 125,
            headerCheckboxSelection: true,
            checkboxSelection: true,
            sortable: true,
            initialSort: 'desc',
            sortIndex: 0,
            initialSortIndex: 0,
            sort: 'desc',
            sortingOrder: ['asc','desc'],
            unSortIcon: false,
            filter: 'agNumberColumnFilter',
            filterParams: {
              filterOptions: [
                'equals',
                'notEqual',
                'lessThan',
                'lessThanOrEqual',
                'greaterThan',
                'greaterThanOrEqual',
                'inRange'
              ]
            },
            resizable: true,
            floatingFilter: true,
            suppressSizeToFit: true,
          },
          {
            headerName: 'Name',
            field: 'name',
            cellRenderer: TextCellRendererComponent,
            sortable: true,
            unSortIcon: true,
            resizable: true,
            filter: 'agTextColumnFilter',
            floatingFilter: true,
            autoHeight: true,
            cellEditor: 'agTextCellEditor',
            type: 'editableColumn',
          },
          {
            headerName: 'Enabled',
            field: 'enabled',
            cellRenderer: TextCellRendererComponent,
            cellRendererParams: {
              onLabel: 'Enabled',
              offLabel: 'Not Enabled',
              isBoolean: true
            },
            sortable: true,
            unSortIcon: true,
            resizable: true,
            filter: 'agSetColumnFilter',
            filterParams: {
              values: [true, false],
              valueFormatter: (params: any) => params.value ? 'Enabled' : 'Not Enabled'
            },
            floatingFilter: true,
            cellEditor: BooleanEditorComponent,
            type: 'editableColumn',
          },
          {
            headerName: 'Created By',
            field: 'createdBy',
            cellRenderer: RefsCellRendererComponent,
            sortable: true,
            unSortIcon: true,
            resizable: true,
            filter: 'agSetColumnFilter',
            filterParams: {
              values: [],
              valueFormatter: (params: any) => {
                return `${params.value.name}`;
              },
              keyCreator: (params: any) => {
                return params.value._id;
              },
              comparator: (a: any, b: any) => this.agGridToolsService.nameASC(a, b),
              isSingleRefFilter: true,
              populate: true,
              select: '_id id name'
            },
            floatingFilter: true,
            autoHeight: true,
          },
          {
            headerName: 'Created At',
            field: 'createdAt',
            sortable: true,
            unSortIcon: true,
            filter: 'agDateColumnFilter',
            filterParams: {
              buttons: ['clear'],
            },
            floatingFilter: true,
            suppressSizeToFit: true,
            cellRenderer: TextCellRendererComponent,
            cellRendererParams: {
              isDate: true,
            },
            valueGetter: this.agGridToolsService.dateValueGetter('createdAt'),
            resizable: true,
            type: 'editableColumn',
            cellEditor: CalendarEditorComponent,
            width: 300,
            autoHeight: true,
            cellStyle: {
              padding: '10px',
              whiteSpace: 'normal',
              lineHeight: '1.5',
            },
            cellClass: 'custom-cell-style',
          },
        ],
        tableParams: {},
        buildParams: {},
        bulkUpdateColDefs: []
      }
    };
  }

  getTableConfig(entity: string): TableConfig {
    const config = this.tableConfigs[entity];
    if (!config) {
      throw new Error(`No configuration found for entity: ${entity}`);
    }
    return config;
  }

  // Helper method to create a new table configuration
  createTableConfig(config: TableConfig): void {
    this.tableConfigs[config.entity] = config;
  }
} 